import type { BreadCrumbJsonLdProps } from 'next-seo'

import { joinURL } from 'ufo'

import type { BreadcrumbProps } from '~/components/atoms/Breadcrumb/Breadcrumb'
import { pageTypes } from '~/utils/urlResolver'

import { getLocalizedHardCodedSlug } from './getLocalizedHardCodedSlug'
import { getSlugFromSlugsByKey } from './getSlugFromArrayByKey'

/**
 * Helper to get a translation from loaded messages
 * @param messages - The messages object loaded via addTranslations
 * @param key - Translation key (e.g., 'breadcrumb.home')
 */
function getTranslation(messages: any, key: string): string {
  const parts = key.split('.')
  let value = messages

  for (const part of parts) {
    if (value && typeof value === 'object' && part in value) {
      value = value[part]
    } else {
      return key // Return key if translation not found
    }
  }

  return typeof value === 'string' ? value : key
}

type MappedSlugs = {
  display: string
  url: string
}

// Defines any breadcrumbs where titles are different to the slug
const URL_MAPPINGS: Record<string, string> = {
  reports: 'market-reports',
}

/**
 * Slugs that sit under a parent page in the breadcrumb trail — a middle
 * crumb linking to the parent is inserted between home and the page
 */
const PARENT_CRUMBS: Record<
  string,
  { i18nKey: string; fallback: string; url: string; item: string }
> = {
  'instant-valuation': {
    i18nKey: 'breadcrumb.valuations',
    fallback: 'Valuations',
    url: '/valuations',
    item: 'valuations',
  },
  'expert-valuation': {
    i18nKey: 'breadcrumb.valuations',
    fallback: 'Valuations',
    url: '/valuations',
    item: 'valuations',
  },
  'replacement-valuation': {
    i18nKey: 'breadcrumb.valuations',
    fallback: 'Valuations',
    url: '/valuations',
    item: 'valuations',
  },
}

const pageTypeKeys = Object.keys(pageTypes)

const trimType = (slug: string) => {
  pageTypeKeys.map((pagetype: string) => {
    slug = slug.replace(`${pagetype}-`, '')
  })
  slug = slug.replace(`series-`, '')
  return slug.replace(/-/g, ' ')
}

/**
 * Prefixes URL with locale if not English
 */
const prefixLocale = (url: string, locale: string): string => {
  if (locale === 'en') return url
  // Avoid double slashes and ensure proper prefix
  const cleanUrl = url.startsWith('/') ? url : `/${url}`
  return `/${locale}${cleanUrl}`
}

export const buildBreadcrumb = (
  slugs: any,
  overrideTitle?: string,
  overrideCollectionTitle?: string,
  messages?: any,
  locale = 'en'
) => {
  // Convert slugs to objects with display and url properties
  const mappedSlugs: MappedSlugs[] = slugs.map((slug: string) => ({
    display: slug,
    url: URL_MAPPINGS[slug] || slug,
  }))

  const isArtist = mappedSlugs[0].display.split('-')[0] === 'artist'
  const home = isArtist
    ? {
        title: messages
          ? getTranslation(messages, 'breadcrumb.artForSale')
          : 'Art for sale',
        url: prefixLocale(
          getLocalizedHardCodedSlug('artworks', locale),
          locale
        ),
        item: '',
      }
    : {
        title: messages ? getTranslation(messages, 'breadcrumb.home') : 'Home',
        url: prefixLocale('/', locale),
        item: '',
      }

  if (mappedSlugs.some(slug => slug.display === 'page')) {
    mappedSlugs.splice(-2)
  }

  if (mappedSlugs.length === 1) {
    const parent = PARENT_CRUMBS[mappedSlugs[0].display]

    return [
      home,
      ...(parent
        ? [
            {
              title: messages
                ? getTranslation(messages, parent.i18nKey)
                : parent.fallback,
              url: prefixLocale(parent.url, locale),
              item: parent.item,
            },
          ]
        : []),
      { title: trimType(mappedSlugs[0].display), item: mappedSlugs[0].url },
    ]
  }

  let breadCrumbArr: any = []
  let url: string[] = []

  mappedSlugs
    .reverse()
    .map((slug: { display: string; url: string }, idx: number) => {
      if (idx <= 0) {
        const item = [...mappedSlugs]
        breadCrumbArr.push({
          title:
            (overrideTitle && overrideTitle) ||
            trimType(slug.display).replace('record prices', 'value'),
          item: item
            .reverse()
            .map(s => s.url)
            .join('/'),
        })
      } else {
        url = []
        for (let i = idx; i < mappedSlugs.length; i++) {
          url.push(mappedSlugs[i].url)
        }

        if (
          slug.display === 'articles' ||
          slug.display === 'guides' ||
          slug.display === 'record-prices' ||
          slug.display === '10-facts'
        ) {
          let urlPath = 'articles'

          const artistSlug = getSlugFromSlugsByKey(
            mappedSlugs.map(s => s.display),
            'artist-'
          )
          urlPath = artistSlug ? `${artistSlug}/articles` : urlPath

          const genreSlug = getSlugFromSlugsByKey(
            mappedSlugs.map(s => s.display),
            'genre-'
          )
          urlPath = genreSlug ? `${genreSlug}/articles` : urlPath

          breadCrumbArr.push({
            title: messages
              ? getTranslation(messages, 'breadcrumb.articles')
              : 'articles',
            url: prefixLocale(`/${urlPath}`, locale),
            item: urlPath,
          })
        } else {
          const stringUrl = url
            .reverse()
            .map(s => s)
            .join('/')

          if (slug.display.includes('the-directory')) {
            breadCrumbArr.push({
              title: messages
                ? getTranslation(messages, 'breadcrumb.theDirectory')
                : 'The Directory',
              url: prefixLocale(`/directory`, locale),
              item: 'the-directory',
            })
            return
          }

          if (slug.display.includes('our-people')) {
            breadCrumbArr.push({
              title: messages
                ? getTranslation(messages, 'breadcrumb.ourPeople')
                : 'Our people',
              url: getLocalizedHardCodedSlug('our-people', locale),
              item: 'our-people',
            })
            return
          }

          if (slug.display.includes('press-and-media')) {
            breadCrumbArr.push({
              title: messages
                ? getTranslation(messages, 'breadcrumb.pressAndMedia')
                : 'Press and Media',
              url: prefixLocale(`/press`, locale),
              item: 'press-and-media',
            })
            return
          }

          const title =
            (slug.display.includes('series-') ||
              slug.display.includes('collection-')) &&
            overrideCollectionTitle
              ? overrideCollectionTitle
              : trimType(slug.display)

          breadCrumbArr.push({
            title: title,
            url: prefixLocale(`/${stringUrl}`, locale),
            item: stringUrl,
          })
        }
      }
    })

  breadCrumbArr = breadCrumbArr.filter((item: any) => item.title !== 'all')

  const index = breadCrumbArr.findIndex((item: any) => item.item === 'articles')

  if (index !== -1) {
    breadCrumbArr.splice(index + 1, 1)
  }

  breadCrumbArr.push(home)
  return breadCrumbArr.reverse()
}

export const getBreadcrumbJsonLd = (breadcrumbArr: BreadcrumbProps['list']) => {
  const ld: BreadCrumbJsonLdProps['itemListElements'] = []

  breadcrumbArr.map((crumb, i) =>
    ld.push({
      position: i + 1,
      name:
        crumb.item.split('/').reverse()[0].split('-').join(' ') || crumb.title,
      item: crumb?.url
        ? joinURL(process.env.NEXT_PUBLIC_SITE_URL || '', crumb.url)
        : null,
    })
  )
  return ld
}
